import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { QR_CONFIG } from "@/lib/constants";
import { trpc } from "@/utils/trpc";

export default function Qr() {
  const { id: siteId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const entranceId = searchParams.get("entrance");

  const site = useQuery({
    ...trpc.getSite.queryOptions({ id: siteId || "" }),
    enabled: Boolean(siteId),
  });

  const entrance = useQuery({
    ...trpc.getEntrancePublic.queryOptions({ id: entranceId || "" }),
    enabled: Boolean(entranceId),
  });

  const value = useMemo(() => {
    if (!siteId) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    // Always encode the public worker sign-in path so it works across subdomains
    const base = origin.replace(/^(https:\/\/)(api-?|api\.)/, "$1");

    if (entranceId) {
      // Generate QR for entrance signin
      return `${base}${QR_CONFIG.ENTRANCE_SIGNIN_PATH_PATTERN}/${entranceId}`;
    } else {
      // Generate QR for site signin
      return `${base}${QR_CONFIG.SITE_SIGNIN_PATH_PATTERN}/${siteId}`;
    }
  }, [siteId, entranceId]);

  if (!siteId) return null;

  const isLoading = entranceId ? entrance.isLoading : site.isLoading;
  const error = entranceId ? entrance.error : site.error;
  const data = entranceId ? entrance.data : site.data;

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <p className="text-sm text-muted-foreground">
          Loading {entranceId ? "entrance" : "site"} QR code…
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <p className="text-sm text-destructive">
          Unable to load {entranceId ? "entrance" : "site"} details for this QR
          code.
        </p>
      </div>
    );
  }

  const entranceSiteName =
    entranceId && "siteName" in data ? data.siteName : undefined;

  return (
    <div className="fixed min-h-svh inset-0 bg-white pt-36 overflow-scroll print:overflow-visible print:p-0 print:m-0 print:inset-0 print:static print:flex print:flex-col print:items-center print:justify-center">
      <p className="print:hidden absolute top-0 left-0 right-0 text-center bg-primary text-primary-foreground text-sm p-4">
        Please print this QR code and display it at the{" "}
        {entranceId ? "entrance" : "site entrance"}.
        <br />
        (This banner won't be in the printed version)
      </p>
      <div className="mb-8 text-center space-y-2 md:space-y-4">
        <h1 className="font-display text-2xl font-bold uppercase md:text-4xl">
          Scan to Sign In
        </h1>
        {entranceId ? (
          <>
            <h2 className="text-2xl md:text-3xl font-bold px-4 py-2 bg-gray-100">
              {data.name}
            </h2>
            <h3 className="text-xl md:text-2xl font-bold">
              {entranceSiteName}
            </h3>
          </>
        ) : (
          <h2 className="text-3xl md:text-5xl font-bold px-4 py-2 bg-gray-100">
            {data.name}
          </h2>
        )}
        {data.plusCode || (!entranceId && data.plusCode) ? (
          <p className="text-base md:text-lg text-muted-foreground">
            Plus Code:{" "}
            <span className="font-meta font-semibold">{data.plusCode}</span>
          </p>
        ) : null}
        <p className="print:hidden text-sm text-muted-foreground max-w-xl mx-auto px-4">
          Show this QR code at the gate. Crews can also navigate by searching
          the Plus Code in Google Maps or Apple Maps.
        </p>
      </div>
      <div className="flex items-center justify-center [&_svg]:max-w-full [&_svg]:h-auto">
        <QRCodeSVG value={value} size={640} includeMargin level="M" />
      </div>
    </div>
  );
}
